import React, { useEffect, useState } from "react";
import axios from "axios";
import { LoadingSpinner } from "../Notifications/LoadingSpinner";
import TableComponent from "../Common/componets/TableComponent";
import { useQuery } from "react-query";
import { fetchCarBookings, getAllUCBookings } from "../../utility/api";
import { useTranslation } from "react-i18next";
import Joi from "joi";
import PaginationComponent from "../Common/componets/PaginationComponent";
import FilterDropdown from "../Common/componets/FilterComponent";
import { debounce } from "../../utility/utils";
import BookingSteps from "../UsedCars/BookingSteps";
import { Dialog } from "@headlessui/react";
import { AiOutlineLoading } from "react-icons/ai";
import { icons8crossFilled } from "../../assets";
import axiosInstance, { notifySuccess } from "../../utility/axiosInstance";
import { toast } from "react-toastify";
import BackArrowButton from "../Common/componets/backArrowButton";

const UsedCarBookingHistory = () => {
  const { t } = useTranslation();
  const [paginationIndex, setPaginationIndex] = useState({
    index: 1,
    limit: 10,
  });
  const [loading, setLoading] = useState(true);
  const [previous, setPrevious] = React.useState([]);
  const [selectedValues, setSelectedValues] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [carDealStatus, setCarDealStatus] = useState({
    isActive: false,
    data: "",
    _id: "",
  });
  const [carBookingData, setCarBookingData] = useState([]);
  const [isBookModal, setBookModal] = useState(false);
  const [isReject, setIsReject] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery(
    ["getAllUCBookings", paginationIndex.index, selectedValues],
    () =>
      getAllUCBookings(
        paginationIndex.index,
        paginationIndex.limit,
        selectedValues,
        searchValue
      ),
    {
      refetchOnWindowFocus: true,
      refetchOnReconnect: false,
      refetchOnMount: false,
    }
  );

  useEffect(() => {
    if (searchValue !== null) {
      const debouncedSetLoading = debounce(() => refetch(), 800);
      debouncedSetLoading();
    }
  }, [searchValue, refetch]);

  useEffect(() => {
    const bookings = data?.data?.bookings;
    if (bookings) {
      const processedData = bookings?.map((item, index) => ({
        srNo: index + 1,
        carName: `${item.car.brand} ${item.car.model}`,
        userName: `${item.user?.firstName} ${item.user?.lastName}`,
        status: item.status,
        date: item.testDrive.date,
      }));
      setPrevious(processedData);
    }
  }, [data]);

  useEffect(() => {
    if (!isLoading && loading) {
      const debouncedSetLoading = debounce(() => setLoading(false), 500);
      debouncedSetLoading();
    }
  }, [isLoading]);
  // console.log(paginationIndex.index, paginationIndex.limit);
  if (isLoading && loading) return <LoadingSpinner />;

  if (isError)
    return (
      <div className="text-center py-4 text-red-500">Error loading data</div>
    );

  const defaultColumns = [
    {
      accessorKey: "srNo",
      header: t("bookServices.serviceRequestTable.columns.srNo"),
      cell: (info) => info.row.index + 1,
    },
    {
      accessorKey: "carName",
      header: t("bookServices.serviceRequestTable.columns.car"),
      cell: (info) => `${info.row.original?.carName ?? "N/A"}`,
    },
    {
      accessorKey: "userName",
      header: t("bookServices.serviceRequestTable.columns.name"),
      cell: (info) => `${info.row.original?.userName || "N/A"}`,
    },
    {
      accessorKey: "status",
      header: t("bookServices.serviceRequestTable.columns.status"),
      cell: (info) => t(`usedCars.table.${info.row.original?.status}`),
    },
    {
      accessorKey: "date",
      header: t("bookServices.serviceRequestTable.columns.bookingDate"),
      cell: (info) =>
        info.row.original?.date
          ? new Date(info.row.original.date).toLocaleDateString()
          : "N/A",
    },
    {
      accessorKey: "statusHistory",
      header: t("services.action"),
      cell: (info) => (
        <p
          className="cursor-pointer text-blue-500"
          onClick={() => {
            setCarDealStatus({
              isActive: true,
              data: `${info.getValue()} ${t("usedCars.table.at")}`,
              _id: info.row.original._id,
              booking: info.row.original.booking,
              currentStatus: info.getValue(),
              buyerDetail: info.row.original.booking.buyer,
              carDetail: info.row.original.booking.car,
              cancellationReason: info?.row?.original?.cancellationReason,
            });
            if (info.getValue() == "rejected") {
              setIsReject(true);
            }
          }}
        >
          {t("viewImage")}
        </p>
      ),
    },
  ];

  const schema = Joi.object({
    bookings: Joi.array()
      .items(
        Joi.object({
          _id: Joi.string().required(),
          cancellationReason: Joi.string().allow(null, ""),
          car: Joi.object({
            overview: Joi.object({
              carName: Joi.string().required(),
              carModel: Joi.string().required(),
            }).unknown(true),
          })
            .unknown(true)
            .required(),
          buyer: Joi.object({
            firstName: Joi.string().required(),
            lastName: Joi.string().required(),
          })
            .unknown(true)
            .allow(null),
          status: Joi.string().required(),
          testDrive: Joi.object({
            date: Joi.string().isoDate().required(),
          })
            .unknown(true)
            .required(),
        }).unknown(true)
      )
      .required(),
    totalRecords: Joi.number().integer().required(),
  }).unknown(true);

  const { bookings, totalRecords } = (() => {
    const validationResult = schema.validate(data?.data ?? {}, {
      abortEarly: false,
    });
    if (validationResult.error && !loading) {
      console.error("Validation Error:", validationResult.error.details);
      return {
        bookings: [],
        totalRecords: 0,
        errorMessage: "Data validation failed. Please try again later.",
      };
    }
    return validationResult.value;
  })();

  if (data?.data?.errorMessage) {
    return (
      <div className="text-center py-4 text-red-500">
        {data?.data?.errorMessage}
      </div>
    );
  }

  const processedData =
    bookings?.map((item, index) => ({
      booking: item,
      srNo: index + 1,
      carName: `${item?.car?.overview?.carName} ${item?.car?.overview?.carModel}`,
      userName: `${item.buyer?.firstName} ${item.buyer?.lastName}`,
      status: item.status,
      statusHistory: item.status,
      date: item.testDrive.date,
    })) || previous;

  const filteredOptions = [
    "pending",
    "accepted",
    "rejected",
    "cancelled",
    "reserved",
    "dealClosed",
  ].map((status) => ({
    label: t(`usedCars.table.${status}`),
    value: status,
  }));
  const handleUpdate = async (status, updatedData) => {
    setLoading(true);

    try {
      const payload = {};
      // return

      if (updatedData == null) {
        toast.warn(t("UnauthorizedAccess.title"));
        return;
      }

      if (!carDealStatus?.booking?._id) {
        toast.warn(t("UnauthorizedAccess.title"));
        return;
      }

      const { ...rest } = updatedData;
      Object.assign(payload, rest);
      payload.status = status.status;

      const response = await axiosInstance.put(
        `usedCars/bookings/${carDealStatus.booking._id}`,
        // {
        //   status: status || updatedData?.status,
        //   cancellationReason: updatedData?.cancellationReason || "",
        //   proposedDate: updatedData?.proposedDate || "",
        //   remarks: updatedData?.remarks || "",
        //   generatePaymentLink: {
        //     amount: updatedData?.generatePaymentLink?.amount || 200000,
        //   },
        // }
        payload
      );
      if (response.data) {
        notifySuccess(response?.data?.message);

        setCarDealStatus({
          ...carDealStatus,
          currentStatus: response.data.booking.status,
          cancellationReason: response.data.booking.cancellationReason,
          booking: response?.data.booking,
        });

        setIsReject(false);

        // currentStep(response.data.booking.status);
      } else {
        console.error("Error updating status", response);
      }
    } catch (error) {
      // notifyError(`Error updating status ${error}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Dialog
        open={carDealStatus.isActive}
        onClose={() => setCarDealStatus({ isActive: false })}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/70 flex w-screen items-center justify-center p-4">
          <Dialog.Panel className="w-full flex justify-center items-center rounded">
            <div className="min-h-[55vh] w-[700px] rounded-md relative bg-[#F5F5F5] p-4">
              <div>
                <div className="p-2">
                  <img
                    src={icons8crossFilled}
                    alt=""
                    onClick={() => setCarDealStatus({ isActive: false })}
                    className="w-8 h-8 absolute flex justify-center items-center right-2 top-2 rounded-full cursor-pointer"
                  />
                </div>
              </div>
              <div className="mt-0">
                <div className="">
                  <h1 className="text-2xl p-2 text-center">
                    {t("car_booking_status")}
                  </h1>
                </div>
                <div className="">
                  {!data ? (
                    <div className="border min-h-[500px] flex items-center justify-center">
                      <AiOutlineLoading className="h-6 w-6 animate-spin m-auto" />
                    </div>
                  ) : (
                    <BookingSteps
                      currentStatus={0}
                      onUpdate={handleUpdate}
                      data={carDealStatus}
                      setData={setCarDealStatus}
                      setIsReject={setIsReject}
                      isReject={isReject}
                      loading={loading}
                      buttonLoading={false}
                    />
                  )}
                </div>
              </div>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      <div className="min-h-screen px-10 mb-20 ">
        {isLoading ? <LoadingSpinner /> : null}
        <div className="flex items-end">
          <BackArrowButton />
          <h1 className="text-2xl font-bold text-center py-4 m-auto">
            {t("home.usedCarHistory")}
          </h1>
        </div>
        <FilterDropdown
          filteredOptions={filteredOptions}
          setSelectedValues={setSelectedValues}
          selectedValues={
            selectedValues ? t(`usedCars.table.${selectedValues}`) : ""
          }
          setSearchValue={setSearchValue}
          searchValue={searchValue}
        />
        <TableComponent
          columns={defaultColumns}
          data={processedData?.length > 0 ? processedData : previous}
          paginationIndex={paginationIndex}
          setPaginationIndex={setPaginationIndex}
          totalCount={totalRecords}
          pageSize={10}
        />
        <PaginationComponent
          pageIndex={paginationIndex.index}
          pageCount={
            processedData?.length > 0 ? Math.ceil(totalRecords / 10) : 0
          }
          onPageChange={(page) =>
            setPaginationIndex((prev) => ({ ...prev, index: page }))
          }
        />
      </div>
    </div>
  );
};

export default UsedCarBookingHistory;
